import type { TaxonomyTag } from '@/lib/services/taxonomy-tags/taxonomy-tags.types'
import type { MerchandisingTagGroup, MerchandisingTagGroupKey } from './merchandising-tags.types'

interface MerchandisingTagGroupDefinition {
  key: string
  label: string
}

const MERCHANDISING_TAG_GROUPS = {
  holiday: { key: 'holiday', label: 'Holidays' },
  season: { key: 'season', label: 'Seasons' },
  everyday: { key: 'everyday', label: 'Everyday Occasions' },
} satisfies Record<MerchandisingTagGroupKey, MerchandisingTagGroupDefinition>

/** The option fields that place it in a group: a flattened group, or its tag type and kind. */
export interface MerchandisingTagGroupingFields {
  group_key?: string | null | undefined
  group_label?: string | null | undefined
  tag_type?: string | null | undefined
  kind?: string | null | undefined
}

/** A tag as the pickers list it: its group copied onto it and `label` copied to `name`. */
export type FlattenedMerchandisingTag<TTag> = TTag & {
  group_key: MerchandisingTagGroupKey
  group_label: string
  name: string
}

/** Options grouped for display; a flattened option may name a group the API did not send. */
export interface MerchandisingTagOptionGroup<TOption> {
  key: string
  label: string
  options: TOption[]
}

const isMerchandisingTagGroupKey = (key: string): key is MerchandisingTagGroupKey =>
  Object.hasOwn(MERCHANDISING_TAG_GROUPS, key)

const knownGroupLabel = (key: string) =>
  isMerchandisingTagGroupKey(key) ? MERCHANDISING_TAG_GROUPS[key].label : undefined

function merchandisingTagGroup(
  option: MerchandisingTagGroupingFields
): MerchandisingTagGroupDefinition {
  if (option?.group_key) {
    return {
      key: option.group_key,
      label: option.group_label ?? knownGroupLabel(option.group_key) ?? 'Occasions',
    }
  }

  if (option?.tag_type === 'season') return MERCHANDISING_TAG_GROUPS.season
  if (option?.kind === 'date_backed') return MERCHANDISING_TAG_GROUPS.holiday

  return MERCHANDISING_TAG_GROUPS.everyday
}

export function flattenMerchandisingTagGroups<TTag extends Pick<TaxonomyTag, 'label'>>(
  groups: MerchandisingTagGroup<TTag>[] = []
): FlattenedMerchandisingTag<TTag>[] {
  return groups.flatMap((group) =>
    (group?.tags ?? []).map((tag) => ({
      ...tag,
      group_key: group.key,
      group_label: group.label,
      name: tag.label,
    }))
  )
}

export function groupMerchandisingTagOptions<TOption extends MerchandisingTagGroupingFields>(
  options: TOption[] = []
): MerchandisingTagOptionGroup<TOption>[] {
  const groups = options.reduce<MerchandisingTagOptionGroup<TOption>[]>(
    (groupedOptions, option) => {
      const groupDefinition = merchandisingTagGroup(option)
      let group = groupedOptions.find((candidate) => candidate.key === groupDefinition.key)

      if (!group) {
        group = { ...groupDefinition, options: [] }
        groupedOptions.push(group)
      }

      group.options.push(option)

      return groupedOptions
    },
    []
  )

  const groupOrder = Object.keys(MERCHANDISING_TAG_GROUPS)

  return groups.sort((firstGroup, secondGroup) => {
    const firstIndex = groupOrder.indexOf(firstGroup.key)
    const secondIndex = groupOrder.indexOf(secondGroup.key)

    return (
      (firstIndex < 0 ? groupOrder.length : firstIndex) -
      (secondIndex < 0 ? groupOrder.length : secondIndex)
    )
  })
}

export function isSeasonalContentTag(
  tag: Pick<MerchandisingTagGroupingFields, 'tag_type' | 'kind'> | null | undefined
): boolean {
  return tag?.tag_type === 'season' || tag?.kind === 'date_backed'
}

export function filterSeasonalContentTagGroups<TTag extends MerchandisingTagGroupingFields>(
  groups: MerchandisingTagGroup<TTag>[] = []
): MerchandisingTagGroup<TTag>[] {
  return groups
    .map((group) => ({
      ...group,
      tags: (group?.tags ?? []).filter(isSeasonalContentTag),
    }))
    .filter((group) => group.tags.length > 0)
}

export function flattenSeasonalContentTagGroups<
  TTag extends MerchandisingTagGroupingFields & Pick<TaxonomyTag, 'label'>,
>(groups: MerchandisingTagGroup<TTag>[] = []): FlattenedMerchandisingTag<TTag>[] {
  return flattenMerchandisingTagGroups(filterSeasonalContentTagGroups(groups))
}

/** Adds selected options the loaded groups lack, so a selection never disappears from the list. */
export function mergeSelectedMerchandisingTagGroups<
  TTag extends MerchandisingTagGroupingFields & Pick<TaxonomyTag, 'id' | 'label'>,
  TSelectedOption extends MerchandisingTagGroupingFields & {
    id?: number | string | null | undefined
  },
>(
  groups: MerchandisingTagGroup<TTag>[] = [],
  selectedOptions: TSelectedOption[] = []
): { key: string; label: string; tags: (FlattenedMerchandisingTag<TTag> | TSelectedOption)[] }[] {
  const knownIds = new Set(
    groups.flatMap((group) => group?.tags ?? []).map((option) => Number(option.id))
  )
  const unresolvedSelectedOptions = selectedOptions.filter((option) => {
    const id = Number(option?.id)

    if (!(id > 0) || knownIds.has(id)) return false

    knownIds.add(id)
    return true
  })

  return groupMerchandisingTagOptions([
    ...flattenMerchandisingTagGroups(groups),
    ...unresolvedSelectedOptions,
  ]).map((group) => ({
    key: group.key,
    label: group.label,
    tags: group.options,
  }))
}

/** Ids arrive as numbers from payloads and as strings from form fields. */
type MerchandisingTagIdInput = number | string

/**
 * Splits picker ids into occasion and season ids. Current ids missing from the loaded options stay
 * selected rather than being dropped.
 */
export function splitMerchandisingTagIds(
  ids: MerchandisingTagIdInput[] = [],
  options: Pick<TaxonomyTag, 'id' | 'tag_type'>[] = [],
  {
    occasionIds: currentOccasionIds = [],
    seasonIds: currentSeasonIds = [],
  }: { occasionIds?: MerchandisingTagIdInput[]; seasonIds?: MerchandisingTagIdInput[] } = {}
): { occasionIds: number[]; seasonIds: number[] } {
  const selectedIds = new Set(ids.map(Number))
  const selectedOptions = options.filter((option) => selectedIds.has(Number(option.id)))
  const classifiedOptionIds = new Set(
    options
      .filter((option) => option.tag_type === 'occasion' || option.tag_type === 'season')
      .map((option) => Number(option.id))
  )
  const preserveUnresolvedIds = (currentIds: MerchandisingTagIdInput[]) =>
    currentIds.map(Number).filter((id) => selectedIds.has(id) && !classifiedOptionIds.has(id))

  return {
    occasionIds: [
      ...selectedOptions
        .filter((option) => option.tag_type === 'occasion')
        .map((option) => Number(option.id)),
      ...preserveUnresolvedIds(currentOccasionIds),
    ],
    seasonIds: [
      ...selectedOptions
        .filter((option) => option.tag_type === 'season')
        .map((option) => Number(option.id)),
      ...preserveUnresolvedIds(currentSeasonIds),
    ],
  }
}
